import {Modal} from 'antd';
import {useCallback, useEffect, useState} from 'react';

import {Spinner} from '../../../../../../../../../layout/spinner/spinner';
import {Locale} from '../../../../../../../../../provider/locale/localization';
import {LangKeyType} from '../../../../../../../../../provider/locale/translation/type';
import {usePostGenerationOptions} from '../../../../../../../../../service/posts/posts-hooks';
import {
    PostAiSuggestApplyType,
    PostAiSuggestHookType,
    PostSourceEnum,
} from '../../../../../../../../../service/posts/posts-types';

import {buildGenerationSettings, createSettingsDraft} from './ai-generation-helper';
import {AiGenerationModalFooter} from './ai-generation-modal-footer';
import {AiGenerationSettingsDraftType, AiGenerationStepEnum} from './ai-generation-type';
import {GenerationResultStep} from './generation-result-step';
import {GenerationSettingsStep} from './generation-settings-step';
import * as styles from './ai-generation-modal.scss';

type PropsType = {
    suggestSettings: PostAiSuggestHookType;
    onApplySuggest: (value: PostAiSuggestApplyType) => Promise<void> | void;
    onClose: () => void;
    catalog?: PostSourceEnum;
};

export function AiGenerationModal(props: PropsType): JSX.Element {
    const {suggestSettings, onApplySuggest, onClose} = props;

    const {
        suggests,
        images,
        activeSuggestIndex,
        setActiveSuggestIndex,
        isGeneratingSuggest,
        generatingImageIndex,
        requestSuggest,
        requestMoreSuggests,
        regenerateImage,
        deleteImage,
        editText,
        lastSettings,
    } = suggestSettings;

    const {data: generationOptions, isInitialLoading: isLoadingOptions} = usePostGenerationOptions({enabled: true});

    const [step, setStep] = useState<AiGenerationStepEnum>(
        suggests.length > 0 ? AiGenerationStepEnum.Result : AiGenerationStepEnum.Settings
    );

    const [settingsDraft, setSettingsDraft] = useState<AiGenerationSettingsDraftType>(
        createSettingsDraft(lastSettings)
    );

    const [isApplying, setIsApplying] = useState<boolean>(false);

    // on text generation finish: show results, or get back to settings if nothing came (error case)
    useEffect(() => {
        if (step === AiGenerationStepEnum.Loading && !isGeneratingSuggest) {
            setStep(suggests.length > 0 ? AiGenerationStepEnum.Result : AiGenerationStepEnum.Settings);
        }
    }, [isGeneratingSuggest, step, suggests.length]);

    const activeSuggest = suggests[activeSuggestIndex] || null;
    const activeText = activeSuggest?.text ?? '';
    const activeImage = images[activeSuggestIndex] ?? null;

    const isImageGenerationAvailable = Boolean(generationOptions?.imageEnabled);

    const handleGenerate = useCallback(() => {
        if (!settingsDraft.postType) {
            return;
        }

        requestSuggest(
            buildGenerationSettings({
                draft: settingsDraft,
                postType: settingsDraft.postType,
                isImageGenerationAvailable,
            })
        );

        setStep(AiGenerationStepEnum.Loading);
    }, [isImageGenerationAvailable, requestSuggest, settingsDraft]);

    const handleApply = useCallback(async () => {
        if (!activeSuggest) {
            return;
        }

        setIsApplying(true);

        try {
            await onApplySuggest({
                text: activeText,
                image: activeImage,
                cta: activeSuggest.cta ?? null,
            });
            onClose();
        } finally {
            setIsApplying(false);
        }
    }, [activeImage, activeSuggest, activeText, onApplySuggest, onClose]);

    const titleKey: LangKeyType =
        step === AiGenerationStepEnum.Settings ? 'POSTS_AI_GENERATION__SETUP__TITLE' : 'POSTS_SUGGESTS__TITLE';

    return (
        <Modal
            closable
            footer={
                <AiGenerationModalFooter
                    hasActiveSuggest={Boolean(activeSuggest)}
                    isApplying={isApplying}
                    isGeneratingSuggest={isGeneratingSuggest}
                    isSubmitDisabled={!settingsDraft.postType || isLoadingOptions}
                    onApply={handleApply}
                    onBack={() => setStep(AiGenerationStepEnum.Settings)}
                    onGenerate={handleGenerate}
                    onGenerateMore={requestMoreSuggests}
                    step={step}
                />
            }
            onCancel={onClose}
            open
            title={<Locale stringKey={titleKey} />}
            width={848}
        >
            {step === AiGenerationStepEnum.Settings && (
                <GenerationSettingsStep
                    isLoadingOptions={isLoadingOptions}
                    onChange={setSettingsDraft}
                    options={generationOptions}
                    value={settingsDraft}
                />
            )}

            {step === AiGenerationStepEnum.Loading && (
                <div className={styles.AiGenerationModal_loading}>
                    <Spinner />
                    <Locale stringKey="POSTS_AI_GENERATION__LOADING" />
                </div>
            )}

            {step === AiGenerationStepEnum.Result && (
                <GenerationResultStep
                    activeSuggestIndex={activeSuggestIndex}
                    image={activeImage}
                    imageEnabled={Boolean(lastSettings?.generateImage)}
                    isRegeneratingImage={generatingImageIndex === activeSuggestIndex}
                    onChangeActiveSuggestIndex={setActiveSuggestIndex}
                    onDeleteImage={() => deleteImage(activeSuggestIndex)}
                    onEditText={(newText) => editText(activeSuggestIndex, newText)}
                    onRegenerateImage={() => regenerateImage(activeSuggestIndex)}
                    text={activeText}
                    totalSuggests={suggests.length}
                />
            )}
        </Modal>
    );
}
